import { useEffect } from 'react';
import { useLocation, useMatches } from 'react-router';
import { useRecentPagesStore } from '@/store/recent-pages';

/** Paths excluded from recent-pages tracking. */
const SKIP_PREFIXES = ['/login', '/change-password', '/fee/receipts/'];

/** Resolve a human label from the deepest match's `handle.crumb`. */
function labelFromMatches(matches: ReturnType<typeof useMatches>, pathname: string): string {
  for (let i = matches.length - 1; i >= 0; i--) {
    const m = matches[i];
    const h = m.handle as { crumb?: string | ((p: Record<string, unknown>) => string) } | undefined;
    if (!h?.crumb) continue;
    if (typeof h.crumb === 'string') return h.crumb;
    try {
      return h.crumb(m.params as Record<string, unknown>);
    } catch {
      // Fall through to next match if param resolution blows up.
    }
  }
  return pathname;
}

/**
 * Records every visit to the AppLayout routes in the recent-pages ring. Mount
 * once near the app root (already done in <AppLayout>); duplicates are deduped
 * by pathname inside the store.
 *
 * The label is captured at *visit* time so the palette can display it without
 * walking the route tree again later.
 */
export function useTrackRecentPage() {
  const location = useLocation();
  const matches = useMatches();
  const push = useRecentPagesStore((s) => s.push);

  useEffect(() => {
    const path = location.pathname;
    if (SKIP_PREFIXES.some((p) => path.startsWith(p))) return;
    if (path === '/' || path === '/dashboard') return; // dashboard is always one keystroke away
    const label = labelFromMatches(matches, path);
    push({ pathname: path, label });
    // matches is referentially unstable per render; we only want to track
    // changes of pathname here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
}
